import { ViewportRuler } from '@angular/cdk/scrolling';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  NgZone,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';

import { RenderEvent } from '®shared/render-event';

import { VirtualisationService } from './virtualisation.service';

export const VIEWPORT_BUFFER_PIXELS = 500;

export enum ViewportStatus {
  OUTSIDE = 'OUTSIDE',
  IN_VIEWPORT = 'IN_VIEWPORT',
  IN_BUFFER = 'IN_BUFFER',
}

/**
 * The virtualiser component manages components based on whether they are
 * within the viewport. Two templates are required via `ContentChild`:
 * - A real component, which is usually expensive and dynamic;
 * - A placeholder component, which is usually cheap and static.
 *
 * This component manages which of the components is presented based on
 * viewport position: "real" components are shown when inside the viewport;
 * "placeholder" components are shown when outside the viewport.
 *
 * Note that the virtualiser also detaches all child components from the
 * change-detection hierarchy, then manages their attachment as they
 * enter or leave the viewport.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'rl-virtualiser',
  templateUrl: './virtualiser.component.html',
})
export class VirtualiserComponent implements OnInit, OnDestroy, AfterViewInit {
  ViewportStatus = ViewportStatus;

  @ContentChild('childComponentTemplate', { static: true })
  childComponentTemplate: TemplateRef<any>;

  @ContentChild('placeholderComponentTemplate', { static: true })
  placeholderComponentTemplate: TemplateRef<any>;

  @ViewChild('renderedRealChild') renderedRealChild: ElementRef;

  viewportStatus = ViewportStatus.OUTSIDE;
  cachedHeight: number;

  private reRenderSubscription: Subscription;

  constructor(
    private cd: ChangeDetectorRef,
    private elementRef: ElementRef,
    private ngZone: NgZone,
    private viewportRuler: ViewportRuler,
    private virtualisationService: VirtualisationService,
  ) {
    this.onDimensionChange = this.onDimensionChange.bind(this);
  }

  ngOnInit() {
    this.reRenderSubscription =
      this.virtualisationService.forceRerender$.subscribe(renderEvent => {
        switch (renderEvent) {
          case RenderEvent.RE_RENDER:
            setTimeout(() => {
              this.recheckViewport();
            });
            break;
          case RenderEvent.WINDOW_SCROLL:
            this.ngZone.run(() => {
              this.recheckViewport();
            });
            break;
          case RenderEvent.WINDOW_RESIZE:
            this.recheckViewport();
            this.recomputeHeight();
            break;
          default:
            break;
        }
      });
  }

  ngOnDestroy() {
    this.reRenderSubscription.unsubscribe();
  }

  ngAfterViewInit() {
    this.cd.detach();
    setTimeout(() => {
      this.recheckViewport();
    });
  }

  recheckViewport() {
    if (this.cd['destroyed']) {
      return;
    }
    const virtualiserRect = this.getVirtualisedClientBoundingRect();
    const isElementVisible = this.isElementVisible(virtualiserRect);
    if (!isElementVisible && this.viewportStatus === ViewportStatus.OUTSIDE) {
      // already-hidden elements need no change-detection
      return;
    } else if (!isElementVisible) {
      // newly-hidden elements use change-detection to display placeholder
      this.viewportStatus = ViewportStatus.OUTSIDE;
      this.cd.detach();
      this.cd.detectChanges();
      return;
    }

    const nextViewportStatus = this.viewportStatusForScroll(virtualiserRect);
    if (this.viewportStatus !== nextViewportStatus) {
      this.viewportStatus = nextViewportStatus;
      if (nextViewportStatus === ViewportStatus.IN_VIEWPORT) {
        this.cd.detectChanges();
        this.cd.reattach();
      } else {
        this.cd.detach();
        this.cd.detectChanges();
      }
    }
  }

  getVirtualisedClientBoundingRect(): ClientRect | null {
    if (
      !this.elementRef ||
      !this.elementRef.nativeElement ||
      !this.elementRef.nativeElement.getBoundingClientRect
    ) {
      return null;
    }
    return this.elementRef.nativeElement.getBoundingClientRect();
  }

  /**
   * Elements that have been hidden (e.g. by filters) should not be considered
   * for change-detection. Hidden elements have zero height.
   */
  private isElementVisible(virtualiserRect: ClientRect | null): boolean {
    return virtualiserRect ? virtualiserRect.height > 0 : false;
  }

  /**
   * Caches the height of the real rendered child element, so that the placeholder can be
   * forced to have an identical height.
   */
  recomputeHeight() {
    if (!this.renderedRealChild) {
      return;
    }

    const { height } =
      this.renderedRealChild.nativeElement.getBoundingClientRect();

    if (this.cachedHeight !== height) {
      // changes in height require change-detection
      this.cachedHeight = height;
      this.cd.detectChanges();
    }
  }

  onDimensionChange() {
    this.recomputeHeight();
  }

  private viewportStatusForScroll(
    virtualiserRect: ClientRect | null,
  ): ViewportStatus {
    if (!virtualiserRect || virtualiserRect.bottom < -VIEWPORT_BUFFER_PIXELS) {
      return ViewportStatus.OUTSIDE;
    } else if (virtualiserRect.bottom < 0) {
      return ViewportStatus.IN_BUFFER;
    }
    const viewport = this.viewportRuler.getViewportRect();
    if (virtualiserRect.top < viewport.height) {
      return ViewportStatus.IN_VIEWPORT;
    } else if (virtualiserRect.top < viewport.height + VIEWPORT_BUFFER_PIXELS) {
      return ViewportStatus.IN_BUFFER;
    }
    return ViewportStatus.OUTSIDE;
  }
}
